// Agent onboarding, brand style and integrations.
//
// Reads use the typed GET routes (routers/agent_onboarding.py, routers/integrations.py).
// Every write goes through the action layer, POST /api/actions/{name} with
// { input }, whose envelope data is { kind: 'result', action, output } or
// { kind: 'pending', pending } (routers/actions.py). Nothing here needs a
// confirmation, so a pending reply is treated as an error the page can show.
//
// The onboarding state (OnboardingOutput, services/actions/onboarding.py) is also
// kept in a small module-level cache for the dashboard gate: every action that
// returns a fresh OnboardingOutput (confirm_member, request_help, update_step,
// finish) writes it there, so the gate never redirects on stale data.
import api from './api';
import { extractResponseData } from '../utils/apiResponseHandler';

const PENDING_MESSAGE = 'This change needs a confirmation step that is not available here yet.';

/**
 * Run one action and return its output.
 * Throws a user-facing Error when the backend asks for a confirmation instead.
 */
export async function runAction(name, input = {}) {
  const response = await api.post(`/actions/${name}`, { input });
  const data = extractResponseData(response);
  if (data?.kind === 'pending') {
    throw new Error(PENDING_MESSAGE);
  }
  if (data && typeof data === 'object' && 'output' in data) {
    return data.output;
  }
  return data;
}

const read = async (path) => extractResponseData(await api.get(path));

// ---- onboarding cache (the dashboard gate) -------------------------------------------

/** How long a cached answer is trusted before the gate checks again in the background. */
export const ONBOARDING_MAX_AGE_MS = 5 * 60 * 1000;

let cached = null; // { userId, value, at }
let inflight = null; // { userId, promise }
let version = 0; // bumped by every write, so an older read never overwrites a newer answer

/** The cached OnboardingOutput for this user, or null. `stale` once it is older than the max age. */
export const peekOnboarding = (userId) => {
  if (!cached || cached.userId !== userId || !cached.value) return null;
  return { value: cached.value, stale: Date.now() - cached.at > ONBOARDING_MAX_AGE_MS };
};

/** Store an OnboardingOutput an action returned (keeps the user it was loaded for). */
export const rememberOnboarding = (value, userId = cached?.userId ?? null) => {
  if (!value || typeof value !== 'object' || !Array.isArray(value.steps)) return;
  version += 1;
  cached = { userId, value, at: Date.now() };
};

/** Drop the cached answer; the next gate check reads it again. */
export const forgetOnboarding = () => {
  version += 1;
  cached = null;
  inflight = null;
};

export const getOnboarding = () => read('/agent/onboarding');

/**
 * Read the onboarding state for `userId` and cache it. Concurrent callers share one
 * request; `force` starts a new one even when a read is already under way.
 */
export const loadOnboarding = (userId, { force = false } = {}) => {
  if (!force && inflight && inflight.userId === userId) return inflight.promise;
  const startedAt = version;
  const request = getOnboarding().then((value) => {
    if (version === startedAt) rememberOnboarding(value, userId);
    return value;
  });
  inflight = { userId, promise: request };
  const clear = () => {
    if (inflight && inflight.promise === request) inflight = null;
  };
  request.then(clear, clear);
  return request;
};

const remembering = async (promise) => {
  const output = await promise;
  rememberOnboarding(output);
  return output;
};

// ---- onboarding step 1: Agent ID ------------------------------------------------------

const identity = ({ agentId, firstName, lastName }) => ({
  agent_id: (agentId || '').trim(),
  first_name: (firstName || '').trim(),
  last_name: (lastName || '').trim(),
});

/** { status: 'matched' | 'no_match' | 'ambiguous' | 'claimed_by_other', member, candidates, message, can_request_help } */
export const findMember = (fields) => runAction('agent.onboarding.find_member', identity(fields));

/** OnboardingOutput plus role_granted. `memberKey` picks one of several records; `officeKey` overrides the office. */
export const confirmMember = ({ memberKey, officeKey, ...fields }) => {
  const input = identity(fields);
  if (memberKey) input.member_key = memberKey;
  if (officeKey) input.office_key = officeKey;
  return remembering(runAction('agent.onboarding.confirm_member', input));
};

/** Ask KeyTa to check the details by hand. Returns OnboardingOutput (help_requested true). */
export const requestHelp = ({ note, ...fields }) => {
  const input = identity(fields);
  const trimmed = (note || '').trim();
  if (trimmed) input.note = trimmed;
  return remembering(runAction('agent.onboarding.request_help', input));
};

// ---- onboarding steps -----------------------------------------------------------------

/** change: 'skip' | 'unskip' | 'dismiss' | 'undismiss'. Returns the refreshed onboarding. */
export const updateStep = (stepKey, change) =>
  remembering(runAction('agent.onboarding.update_step', { step_key: stepKey, change }));

/** End the flow (refused until the Agent ID step is done). Returns OnboardingOutput. */
export const finishOnboarding = () => remembering(runAction('agent.onboarding.finish', {}));

// ---- onboarding step 2: ID check (Didit) ----------------------------------------------

/**
 * Open a Didit ID check. Returns { url, status }: send the browser to `url`
 * (Didit's hosted check), which returns to /agent/onboarding?step=identity, or
 * to the Account section of Settings with returnTo 'settings'.
 * Refused with a short reader-facing message when the agent is already verified
 * or the check is unavailable.
 */
export const startIdentityCheck = (returnTo) =>
  runAction('agent.identity.start', returnTo ? { return_to: returnTo } : {});

/**
 * The agent's ID check: { status, verified_at, role_granted }. `status` is
 * not_started | in_progress | pending_review | approved | declined; `role_granted`
 * turns true on the call that gave a self-registered agent the agent role.
 */
export const getIdentityStatus = () => runAction('agent.identity.status', {});

/** Rules the action layer refuses with a sentence written for the reader (BusinessRuleException, 422). */
const READER_FACING_STATUS = new Set([409, 422, 503]);

/**
 * The sentence to show for a failed action: the backend's own message when it is
 * a refusal written for the reader (a business rule, or a service that is
 * unavailable), else `fallback`. Raw errors and stack traces never reach the page.
 */
export const readerMessage = (error, fallback) => {
  const text = typeof error?.message === 'string' ? error.message.trim() : '';
  if (text && READER_FACING_STATUS.has(error?.statusCode) && text.length <= 200) return text;
  return fallback;
};

// ---- brand style ----------------------------------------------------------------------

/** { items: [{ key, name, description, preview_url, palette, fonts, tone, sample_headline }], selected_key } */
export const getBrandTemplates = () => runAction('agent.branding.templates', {});

/** Pick a template (or null to clear). Returns { selected_key }. */
export const chooseBrandTemplate = (templateKey) =>
  runAction('agent.branding.choose', { template_key: templateKey || null });

/** The agent's newest active listing for the style preview, or null (agents only). */
export const getPreviewListing = async () => {
  const output = await runAction('agent.listings.list', { status: 'active', page: 1, page_size: 1 });
  return Array.isArray(output?.items) && output.items.length ? output.items[0] : null;
};

// ---- profile --------------------------------------------------------------------------

export const getProfile = () => read('/agent/profile');

export const updateProfile = (input) => runAction('agent.profile.update', input);

export const acceptVowTerms = () =>
  runAction('agent.onboarding.accept_vow_terms', { accepted: true });

/** The signed-in user as /auth/profile returns it (primary_role, requested_role, ...). */
export const getSignedInProfile = () => read('/auth/profile');

// ---- integrations ---------------------------------------------------------------------

export const getIntegrationsHealth = () => read('/integrations/health');

/** { url, provider }: `url` is the provider's own consent page (built server side with a state bound to the user). */
export const emailConnectUrl = (provider, returnTo) =>
  runAction('integrations.email.connect_url', { provider, return_to: returnTo });

// The OAuth error return does not name the provider, so the connect call remembers it
// (read back once by components/agent/onboarding/useOauthReturn).
const CONNECT_PROVIDER_KEY = 'keyta.mailboxConnectProvider';

/** Remember which provider a connect flow is starting with. */
export const rememberConnectProvider = (provider) => {
  try {
    sessionStorage.setItem(CONNECT_PROVIDER_KEY, provider);
  } catch (_) {
    /* storage unavailable: the return just won't know the provider */
  }
};

/** The provider the last connect flow started with, read once. */
export const takeConnectProvider = () => {
  try {
    const provider = sessionStorage.getItem(CONNECT_PROVIDER_KEY);
    sessionStorage.removeItem(CONNECT_PROVIDER_KEY);
    return provider;
  } catch (_) {
    return null;
  }
};

/**
 * Send the browser to the provider's consent screen (Google or Microsoft); the
 * OAuth callback comes back to `returnTo` with ?connected= or ?error=.
 */
export const startEmailConnect = async (provider, returnTo) => {
  const result = await emailConnectUrl(provider, returnTo);
  if (!result?.url) throw new Error('No connect URL returned');
  rememberConnectProvider(provider);
  window.location.assign(result.url);
};

export const emailDisconnect = (accountId) =>
  runAction('integrations.email.disconnect', { account_id: accountId });

export const emailRefreshNow = (accountId) =>
  runAction('integrations.email.refresh_now', { account_id: accountId });

const agentOnboardingService = {
  runAction,
  getOnboarding,
  loadOnboarding,
  peekOnboarding,
  rememberOnboarding,
  forgetOnboarding,
  findMember,
  confirmMember,
  requestHelp,
  updateStep,
  finishOnboarding,
  startIdentityCheck,
  getIdentityStatus,
  readerMessage,
  getBrandTemplates,
  chooseBrandTemplate,
  getPreviewListing,
  getProfile,
  updateProfile,
  acceptVowTerms,
  getSignedInProfile,
  getIntegrationsHealth,
  emailConnectUrl,
  startEmailConnect,
  emailDisconnect,
  emailRefreshNow,
};

export default agentOnboardingService;
